/**
 * WidgetCanvas - Container for all widgets in the screen
 *
 * This is the main content area of the AppScreenNode. It:
 * - Renders widgets in a vertical stack
 * - Handles widget selection
 * - Provides empty state when no widgets exist
 *
 * Future enhancement: Could use dnd-kit for drag-to-reorder
 */

import { memo, useState, useCallback } from 'react';
import type { AppWidget } from '@deep-agents/shared-types';
import {
  CardWidgetComponent,
  TextWidgetComponent,
  ButtonWidgetComponent,
  ListWidgetComponent,
  SliderWidgetComponent,
  ImageWidgetComponent,
} from './widgets';
import { WIDGET_GAP, DEFAULT_CANVAS_HEIGHT } from './constants';

interface WidgetCanvasProps {
  /** Array of widgets to render */
  widgets: AppWidget[];
  /** Called when widgets change (add, update, delete, reorder) */
  onWidgetsChange: (widgets: AppWidget[]) => void;
}

export const WidgetCanvas = memo(function WidgetCanvas({
  widgets,
  onWidgetsChange,
}: WidgetCanvasProps) {
  // Track which widget is selected (for property editing)
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);

  // Handle widget selection
  const handleSelectWidget = useCallback((widgetId: string) => {
    setSelectedWidgetId((prev) => (prev === widgetId ? null : widgetId));
  }, []);

  // Handle widget update
  const handleUpdateWidget = useCallback(
    (widgetId: string, updates: Partial<AppWidget>) => {
      const newWidgets = widgets.map((w) =>
        w.id === widgetId ? ({ ...w, ...updates } as AppWidget) : w
      );
      onWidgetsChange(newWidgets);
    },
    [widgets, onWidgetsChange]
  );

  // Handle widget delete
  const handleDeleteWidget = useCallback(
    (widgetId: string) => {
      const newWidgets = widgets.filter((w) => w.id !== widgetId);
      onWidgetsChange(newWidgets);
      if (selectedWidgetId === widgetId) {
        setSelectedWidgetId(null);
      }
    },
    [widgets, onWidgetsChange, selectedWidgetId]
  );

  // Render the appropriate widget component based on type
  const renderWidget = (widget: AppWidget) => {
    const isSelected = selectedWidgetId === widget.id;
    const commonProps = {
      isSelected,
      onSelect: () => handleSelectWidget(widget.id),
      onUpdate: (updates: Partial<AppWidget>) => handleUpdateWidget(widget.id, updates),
      onDelete: () => handleDeleteWidget(widget.id),
    };

    let widgetElement: React.ReactNode = null;
    switch (widget.type) {
      case 'card':
        widgetElement = <CardWidgetComponent widget={widget} {...commonProps} />;
        break;
      case 'text':
        widgetElement = <TextWidgetComponent widget={widget} {...commonProps} />;
        break;
      case 'button':
        widgetElement = <ButtonWidgetComponent widget={widget} {...commonProps} />;
        break;
      case 'list':
        widgetElement = <ListWidgetComponent widget={widget} {...commonProps} />;
        break;
      case 'slider':
        widgetElement = <SliderWidgetComponent widget={widget} {...commonProps} />;
        break;
      case 'image':
        widgetElement = <ImageWidgetComponent widget={widget} {...commonProps} />;
        break;
      default:
        return null;
    }

    // Wrap each widget in a container with a delete button visible when selected
    return (
      <div key={widget.id} className="relative group">
        {widgetElement}
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteWidget(widget.id);
            }}
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs leading-none shadow-sm transition-colors z-10"
            title="Delete widget"
          >
            x
          </button>
        )}
      </div>
    );
  };

  // Deselect when clicking canvas background
  const handleCanvasClick = useCallback(() => {
    setSelectedWidgetId(null);
  }, []);

  return (
    <div
      className="nodrag nopan relative bg-white rounded-lg overflow-hidden"
      style={{
        minHeight: DEFAULT_CANVAS_HEIGHT / 2,
        padding: WIDGET_GAP,
      }}
      onClick={handleCanvasClick}
    >
      {widgets.length === 0 ? (
        // Empty state
        <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-gray-400">
          <svg
            className="w-12 h-12 mb-3 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          <p className="text-sm font-medium">No widgets yet</p>
          <p className="text-xs mt-1">Click the buttons above to add widgets</p>
        </div>
      ) : (
        // Widget list
        <div
          className="flex flex-col"
          style={{ gap: WIDGET_GAP }}
          onClick={(e) => e.stopPropagation()}
        >
          {widgets
            .sort((a, b) => a.order - b.order)
            .map((widget) => renderWidget(widget))}
        </div>
      )}
    </div>
  );
});
